import React, { useState, useEffect, useRef } from 'react';
import s from './AdminRoles.module.css';

const ROL_LABELS = { 1: 'O\'qituvchi', 2: 'Administrator' };
const ROL_OPTIONS = [
  { value: 1, label: 'O\'qituvchi' },
  { value: 2, label: 'Administrator' },
];

export const AdminRoles = () => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };

  const [roles, setRoles] = useState([]);       // [{ id, user, rol }]
  const [users, setUsers] = useState([]);        // [{ id, username, first_name, last_name, email }]
  const [loading, setLoading] = useState(true);

  // Per-row pending role change
  const [pendingRol, setPendingRol] = useState({});   // { [roleId]: number }
  const [saving, setSaving] = useState({});            // { [roleId]: bool }
  const [saved, setSaved] = useState({});              // { [roleId]: bool }
  const [deleting, setDeleting] = useState({});        // { [roleId]: bool }

  // Add-role form
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // { id, username, ... }
  const [newRol, setNewRol] = useState(1);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const suggestRef = useRef(null);

  // Table search filter
  const [filter, setFilter] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes] = await Promise.all([
        fetch('https://api.aiassessment.uz/api/user-rol/', { headers }),
        fetch('https://api.aiassessment.uz/api/users/', { headers }),
      ]);
      const rolesData = await rolesRes.json();
      const usersData = await usersRes.json();
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getUserInfo = (userId) =>
    users.find(u => u.id === userId) ?? {};

  const fullName = (u) =>
    [u.last_name, u.first_name].filter(Boolean).join(' ') || u.username || `User #${u.id}`;

  // Save changed role
  const handleSaveRol = async (role) => {
    const nextRol = pendingRol[role.id] ?? role.rol;
    if (nextRol === role.rol) return;
    setSaving(p => ({ ...p, [role.id]: true }));
    try {
      const res = await fetch(`https://api.aiassessment.uz/api/user-rol/${role.id}/`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ rol: nextRol }),
      });
      if (res.ok) {
        setRoles(prev => prev.map(r => r.id === role.id ? { ...r, rol: nextRol } : r));
        setPendingRol(p => { const n = { ...p }; delete n[role.id]; return n; });
        setSaved(p => ({ ...p, [role.id]: true }));
        setTimeout(() => setSaved(p => { const n = { ...p }; delete n[role.id]; return n; }), 1500);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(p => ({ ...p, [role.id]: false }));
    }
  };

  // Delete role entry
  const handleDelete = async (roleId) => {
    if (!window.confirm('Bu foydalanuvchining rolini o\'chirmoqchimisiz?')) return;
    setDeleting(p => ({ ...p, [roleId]: true }));
    try {
      const res = await fetch(`https://api.aiassessment.uz/api/user-rol/${roleId}/`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok || res.status === 204) {
        setRoles(prev => prev.filter(r => r.id !== roleId));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(p => ({ ...p, [roleId]: false }));
    }
  };

  // Search users for add-form
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSelectedUser(null);
    setAddError('');
    if (q.length < 2) { setSuggestions([]); return; }
    const lower = q.toLowerCase();
    const matched = users
      .filter(u =>
        u.username?.toLowerCase().includes(lower) ||
        u.first_name?.toLowerCase().includes(lower) ||
        u.last_name?.toLowerCase().includes(lower) ||
        u.email?.toLowerCase().includes(lower)
      )
      .slice(0, 8);
    setSuggestions(matched);
  };

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setSearchQuery(u.username || fullName(u));
    setSuggestions([]);
  };

  // Add new role
  const handleAddRole = async () => {
    if (!selectedUser) { setAddError('Foydalanuvchini tanlang'); return; }
    if (roles.some(r => r.user === selectedUser.id)) {
      setAddError('Bu foydalanuvchida allaqachon rol bor');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      const res = await fetch('https://api.aiassessment.uz/api/user-rol/', {
        method: 'POST',
        headers,
        body: JSON.stringify({ user: selectedUser.id, rol: newRol }),
      });
      if (res.ok) {
        const created = await res.json();
        setRoles(prev => [...prev, created]);
        setSearchQuery('');
        setSelectedUser(null);
        setNewRol(1);
      } else {
        setAddError('Xatolik yuz berdi');
      }
    } catch (e) {
      setAddError('Xatolik yuz berdi');
    } finally {
      setAdding(false);
    }
  };

  // Filtered table rows
  const filteredRoles = roles.filter(r => {
    if (!filter) return true;
    const u = getUserInfo(r.user);
    const lower = filter.toLowerCase();
    return (
      u.username?.toLowerCase().includes(lower) ||
      u.first_name?.toLowerCase().includes(lower) ||
      u.last_name?.toLowerCase().includes(lower) ||
      u.email?.toLowerCase().includes(lower)
    );
  });

  const adminCount = roles.filter(r => r.rol === 2).length;
  const teacherCount = roles.filter(r => r.rol === 1).length;

  return (
    <div className={s.page}>
      <div className={s.header}>
        <h1 className={s.title}>Foydalanuvchi rollari</h1>
      </div>

      {/* Stats */}
      <div className={s.stats}>
        <div className={s.statCard}>
          <div className={s.statNum}>{roles.length}</div>
          <div className={s.statLabel}>Jami</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statNum}>{teacherCount}</div>
          <div className={s.statLabel}>O'qituvchi</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statNum}>{adminCount}</div>
          <div className={s.statLabel}>Administrator</div>
        </div>
      </div>

      {/* Add role form */}
      <div className={s.addForm}>
        <label style={{ flex: 1, minWidth: 220 }}>
          Foydalanuvchi qidirish
          <div className={s.suggestionsWrap} ref={suggestRef}>
            <input
              className={s.addFormInput}
              style={{ width: '100%' }}
              placeholder="Username yoki ism..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoComplete="off"
            />
            {suggestions.length > 0 && (
              <div className={s.suggestions}>
                {suggestions.map(u => (
                  <div
                    key={u.id}
                    className={s.suggestionItem}
                    onMouseDown={() => handleSelectUser(u)}
                  >
                    <div>{u.username}</div>
                    <div className={s.suggestionSub}>{fullName(u)} · {u.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </label>

        <label>
          Rol
          <select
            className={s.addFormSelect}
            value={newRol}
            onChange={e => setNewRol(Number(e.target.value))}
          >
            {ROL_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button className={s.btnAdd} onClick={handleAddRole} disabled={adding}>
            {adding ? '...' : '+ Qo\'shish'}
          </button>
          {addError && <span className={s.error}>{addError}</span>}
        </div>
      </div>

      {/* Table filter */}
      <div className={s.toolbar}>
        <input
          className={s.searchBox}
          placeholder="Jadvalda qidirish..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <p className={s.loading}>Yuklanmoqda...</p>
      ) : (
        <div className={s.tableWrapper}>
          <table className={s.table}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Foydalanuvchi</th>
                <th>Username</th>
                <th>Email</th>
                <th>Joriy rol</th>
                <th>Yangi rol</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.length === 0 && (
                <tr className={s.emptyRow}>
                  <td colSpan={7}>Ma'lumot yo'q</td>
                </tr>
              )}
              {filteredRoles.map((role, idx) => {
                const u = getUserInfo(role.user);
                const pending = pendingRol[role.id] ?? role.rol;
                const changed = pending !== role.rol;
                return (
                  <tr key={role.id}>
                    <td>{idx + 1}</td>
                    <td>{fullName(u)}</td>
                    <td>{u.username ?? `#${role.user}`}</td>
                    <td>{u.email ?? '—'}</td>
                    <td>
                      <span className={`${s.roleBadge} ${role.rol === 2 ? s.roleAdmin : s.roleTeacher}`}>
                        {ROL_LABELS[role.rol] ?? `Rol ${role.rol}`}
                      </span>
                    </td>
                    <td>
                      <select
                        className={s.roleSelect}
                        value={pending}
                        onChange={e => setPendingRol(p => ({ ...p, [role.id]: Number(e.target.value) }))}
                      >
                        {ROL_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className={s.actionCell}>
                        {saved[role.id] ? (
                          <span className={s.savedTick}>✓</span>
                        ) : (
                          <button
                            className={s.btnSave}
                            onClick={() => handleSaveRol(role)}
                            disabled={!changed || saving[role.id]}
                          >
                            {saving[role.id] ? '...' : 'Saqlash'}
                          </button>
                        )}
                        <button
                          className={s.btnDelete}
                          onClick={() => handleDelete(role.id)}
                          disabled={deleting[role.id]}
                        >
                          {deleting[role.id] ? '...' : 'x'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
